import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'
import { cn } from '@/src/utils/styles'

// Define responsive size type
type ResponsiveSize = {
  default?: 'sm' | 'md' | 'lg' // For screens below sm (640px)
  sm?: 'sm' | 'md' | 'lg'
  md?: 'sm' | 'md' | 'lg'
  lg?: 'sm' | 'md' | 'lg'
  xl?: 'sm' | 'md' | 'lg'
  '2xl'?: 'sm' | 'md' | 'lg'
}

const buttonVariants = cva(
  'inline-flex items-center w-max justify-center whitespace-nowrap rounded-full font-medium transition-all active:transition-none active:opacity-70 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'h-8 px-3 py-1.5 gap-2 text-sm',
        md: 'h-12 px-4 py-3 gap-2 text-base',
        lg: 'h-14 p-5 gap-3 text-base',
      },
      variant: {
        ghost:
          'bg-transparent p-0 outline-none focus-visible:before:outline focus-visible:before:outline-2 focus-visible:before:outline-focus focus-visible:before:outline-offset-2 text-link hover:text-link-hover',
        outline:
          'bg-transparent text-button-outline-text border-1 border-button-outline-border hover:bg-button-outline-hover hover:text-button-outline-text-hover',
        secondary:
          'bg-button-secondary text-button-secondary-text hover:bg-button-secondary-hover',
        primary:
          'bg-button-primary text-button-primary-text hover:bg-button-primary-hover',
        danger:
          'bg-danger text-danger-text hover:bg-danger-text hover:text-background',
      },
      squared: {
        true: null,
        false: null,
      },
    },
    compoundVariants: [
      {
        variant: ['outline', 'primary', 'secondary', 'danger'],
        class: 'focus-outer',
      },
      {
        variant: ['ghost'],
        class:
          'relative z-10 before:absolute before:-z-10 before:box-content before:rounded-full before:transition-colors',
      },
      {
        variant: ['ghost'],
        size: 'sm',
        squared: false,
        class: 'h-5 before:-inset-x-3 before:-inset-y-1.5',
      },
      {
        variant: ['ghost'],
        size: 'md',
        squared: false,
        class: 'h-6 before:-inset-x-4 before:-inset-y-3',
      },
      {
        variant: ['ghost'],
        size: 'lg',
        squared: false,
        class: 'h-8 before:-inset-x-5 before:-inset-y-3',
      },
      {
        variant: ['ghost'],
        size: 'sm',
        squared: true,
        class: 'size-6 before:-inset-x-1 before:-inset-y-1',
      },
      {
        variant: ['ghost'],
        size: 'md',
        squared: true,
        class: 'size-6 before:-inset-x-3 before:-inset-y-3',
      },
      {
        variant: ['ghost'],
        size: 'lg',
        squared: true,
        class: 'size-8 before:-inset-x-3 before:-inset-y-3',
      },
    ],
    defaultVariants: {
      variant: 'ghost',
      size: 'md',
      squared: false,
    },
  },
)

// Helper function to generate responsive size classes
function getResponsiveSizeClasses(responsiveSize: ResponsiveSize): string {
  const sizeClasses = {
    sm: 'h-8 px-3 py-1.5 gap-2 text-sm',
    md: 'h-12 px-4 py-3 gap-2 text-base',
    lg: 'h-14 p-5 gap-3 text-xl text-base',
  }

  let classes = ''

  // Default size (screens below sm breakpoint)
  if (responsiveSize.default) {
    classes += sizeClasses[responsiveSize.default]
  }

  // Small breakpoint and up
  if (responsiveSize.sm) {
    classes += ` sm:${sizeClasses[responsiveSize.sm].replace(/\s+/g, ' sm:')}`
  }

  // Medium breakpoint and up
  if (responsiveSize.md) {
    classes += ` md:${sizeClasses[responsiveSize.md].replace(/\s+/g, ' md:')}`
  }

  // Large breakpoint and up
  if (responsiveSize.lg) {
    classes += ` lg:${sizeClasses[responsiveSize.lg].replace(/\s+/g, ' lg:')}`
  }

  // XL breakpoint and up
  if (responsiveSize.xl) {
    classes += ` xl:${sizeClasses[responsiveSize.xl].replace(/\s+/g, ' xl:')}`
  }

  // 2XL breakpoint and up
  if (responsiveSize['2xl']) {
    classes += ` 2xl:${sizeClasses[responsiveSize['2xl']].replace(/\s+/g, ' 2xl:')}`
  }

  return classes
}

export interface ButtonProps
  extends ComponentProps<'button'>,
    Omit<VariantProps<typeof buttonVariants>, 'size'> {
  asChild?: boolean
  size?: 'sm' | 'md' | 'lg' | ResponsiveSize
}

const Button = ({
  className,
  variant,
  size,
  squared,
  asChild = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button'

  // Handle responsive size
  let sizeClasses = ''
  if (typeof size === 'object' && size !== null) {
    sizeClasses = getResponsiveSizeClasses(size)
  }

  const baseClasses = buttonVariants({
    size: typeof size === 'string' ? size : undefined,
    variant,
    squared,
  })

  const finalClasses = cn(baseClasses, sizeClasses && sizeClasses, className)

  return <Comp ref={ref} {...props} className={finalClasses} />
}

interface ButtonDivProps
  extends ComponentProps<'div'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

/** Looks like a button, talks like a button, but is a div. */
const ButtonDiv = ({
  className,
  variant,
  size,
  squared,
  asChild = false,
  ref,
  ...props
}: ButtonDivProps) => {
  const Comp = asChild ? Slot : 'div'
  return (
    <Comp
      ref={ref}
      {...props}
      className={cn(buttonVariants({ size, variant, squared, className }))}
    />
  )
}

Button.displayName = 'Button'
ButtonDiv.displayName = 'ButtonDiv'

export { Button, ButtonDiv }
